import i18next from 'i18next';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {cva} from 'platform/shared';

import {ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent, useId, useRef} from 'react';

import {isNotNil, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {LabelActions} from '../Label/LabelActions';
import {showNotification} from '../Notification/utils/events';
import {PlatformInputWrapper} from '../TextInput/components/PlatformInputWrapper';

export type CodeInputSubmitHandler = (code: number[], options: {resetField: () => void}) => void;

export interface CodeInputProps
  extends Omit<FormControlProps<number[]>, 'value' | 'onChange'>, TestIdProps {
  length: number;
  onSubmit: CodeInputSubmitHandler;
  labelActions?: LabelActions;
  isAutoFocused?: boolean;
}

const codeInputVariants = cva(
  'h-full w-full border-none bg-transparent text-center text-[length:var(--text-size-base)] font-[var(--text-weight-default)] leading-[var(--text-leading-medium)] text-[var(--color-primary)] outline-none disabled:cursor-not-allowed disabled:[-webkit-text-fill-color:var(--color-primary)]'
);

function isCompleteCode(code: (number | null)[]): code is number[] {
  return code.every(isNotNil);
}

export function CodeInput(props: CodeInputProps) {
  const generatedId = useId();
  const id = props.id ?? generatedId;

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const values = useRef<(number | null)[]>(Array.from({length: props.length}, () => null));

  const isInvalid = props.isInvalid || isNotNil(props.errorMessage);

  function updateValue(index: number, newValue: number | null) {
    values.current[index] = newValue;

    if (isCompleteCode(values.current)) {
      props.onSubmit(values.current, {resetField});
    }
  }

  function focusInput(index: number) {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
    }
  }

  const resetField = () => {
    values.current = Array.from({length: props.length}, () => null);
    inputRefs.current.forEach((input) => {
      if (input) {
        input.value = '';
      }
    });
    inputRefs.current[0]?.focus();
  };

  function handleInputChange(index: number) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (value.length > 1) {
        event.target.value = value.slice(-1); // Keep only the last character
        return;
      }

      if (value && !/^\d$/.test(value)) {
        event.target.value = ''; // Clear the input for non-numeric characters
        return;
      }

      const numericValue = value ? parseInt(value, 10) : null;
      updateValue(index, numericValue);

      if (value && index < props.length - 1) {
        focusInput(index + 1);
      }
    };
  }

  function handleKeyDown(index: number) {
    return (event: KeyboardEvent<HTMLInputElement>) => {
      const target = event.currentTarget;

      if (event.key === 'Backspace') {
        if (target.value === '' && index > 0) {
          updateValue(index - 1, null);
          focusInput(index - 1);
          const previousInput = inputRefs.current[index - 1];
          if (previousInput) {
            previousInput.value = '';
          }
        } else {
          updateValue(index, null);
          target.value = '';
        }
      } else if (event.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1);
      } else if (event.key === 'ArrowRight' && index < props.length - 1) {
        focusInput(index + 1);
      }
    };
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text').trim();

    const isNotNumber = !/^\d+$/.test(pastedText);
    const isOverflowing = pastedText.length > props.length;

    if (isNotNumber || isOverflowing) {
      showNotification.error(i18next.t('general.notifications.errorCodeFormat'));
      return;
    }

    const pastedDigits = pastedText.split('');

    for (let i = 0; i < props.length; i++) {
      const digit = pastedDigits[i];
      const numericDigit = isString(digit) ? parseInt(digit, 10) : null;
      updateValue(i, numericDigit);

      const input = inputRefs.current[i];
      if (input) {
        input.value = digit ?? '';
      }
    }

    const nextIndex = Math.min(pastedDigits.length, props.length - 1);
    focusInput(nextIndex);
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.target.select();
    props.onFocus?.(event);
  };

  return (
    <VStack>
      <Show when={isNotNil(props.label)}>
        <Label
          id={id}
          actions={props.labelActions}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          data-testid={suffixTestId('label', props)}
        >
          {props.label}
        </Label>
      </Show>
      <HStack spacing={2} data-testid={suffixTestId('container', props)}>
        {Array.from({length: props.length}, (_, index) => (
          <Box width={8} key={index}>
            <PlatformInputWrapper
              isDisabled={props.isDisabled}
              isInvalid={isInvalid}
              isRecommended={props.isRecommended}
              data-testid={suffixTestId(`wrapper-${index}`, props)}
            >
              <input
                ref={(element) => {
                  inputRefs.current[index] = element;
                }}
                className={codeInputVariants()}
                type="text"
                inputMode="numeric"
                maxLength={1}
                autoComplete="one-time-code"
                disabled={props.isDisabled}
                autoFocus={props.isAutoFocused && index === 0}
                onChange={handleInputChange(index)}
                onKeyDown={handleKeyDown(index)}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={props.onBlur}
                data-testid={suffixTestId(`input-${index}`, props)}
                aria-label={`Digit ${index + 1} of ${props.length}`}
                aria-required={props.isRequired}
                aria-invalid={isInvalid}
              />
            </PlatformInputWrapper>
          </Box>
        ))}
      </HStack>
      <Show when={isNotNilOrEmpty(props.errorMessage) || isNotNilOrEmpty(props.helperText)}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
